import { useCallback, useEffect, useRef, useState } from "react";
import type { PdvVariant } from "@vertex/validators/pdv-pure";
/* Só TIPOS do storage no topo: `import type` é apagado na compilação, então
   isto não arrasta o wasm de ~400 KB pro bundle inicial. O módulo em si entra
   por `import()` dinâmico lá embaixo. */
import type * as CatalogRepo from "../storage/repos/catalog";
import type { PdvStore } from "../storage/types";
import { pdvApi } from "./pdv";

/**
 * Fonte da busca do balcão: o espelho local quando ele existe, o servidor
 * quando não.
 *
 * A preferência pelo local não é sobre offline — é sobre **latência**. Um
 * `LIKE` em 20 mil linhas no SQLite roda em menos de 5 ms; o round-trip que ele
 * substitui custa ~300 ms. No balcão isso é a diferença entre a lista aparecer
 * enquanto o operador digita e ele esperar depois de terminar.
 *
 * O espelho é considerado utilizável só quando tem variante dentro. Cair pro
 * servidor até o primeiro sync terminar evita a tela vazia que faria o operador
 * concluir que a loja não tem produto cadastrado.
 */
export type CatalogSource = "local" | "remoto" | "carregando";

interface Catalogo {
  buscar: (termo: string, signal?: AbortSignal) => Promise<PdvVariant[]>;
  porCodigo: (code: string) => Promise<PdvVariant | null>;
  fonte: CatalogSource;
}

export function useCatalog(): Catalogo {
  const [fonte, setFonte] = useState<CatalogSource>("carregando");
  /* O módulo de storage é pesado (~400 KB de wasm) e carrega sozinho depois do
     primeiro paint. Guardamos o que já resolveu, sem forçar o carregamento. */
  const local = useRef<{ store: PdvStore; repo: typeof CatalogRepo } | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const [{ openStore }, repo] = await Promise.all([
          import("../storage"),
          import("../storage/repos/catalog"),
        ]);
        const store = await openStore();
        if (!vivo) return;
        local.current = { store, repo };
        setFonte((await repo.countVariants(store)) > 0 ? "local" : "remoto");
      } catch {
        /* Sem banco local o terminal segue vendendo pelo servidor — que é todo
           o escopo online de hoje. */
        if (vivo) setFonte("remoto");
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const buscar = useCallback<Catalogo["buscar"]>(
    async (termo, signal) => {
      const l = local.current;
      if (l && fonte === "local") return l.repo.searchLocal(l.store, termo);
      return pdvApi.searchVariants(termo.trim(), signal);
    },
    [fonte],
  );

  /**
   * Código exato — o caminho do leitor. Tenta o local primeiro mesmo quando a
   * fonte é remota: o espelho pode ter o item mesmo antes de ser eleito fonte
   * principal, e acertar sem rede é sempre melhor num bipe.
   */
  const porCodigo = useCallback<Catalogo["porCodigo"]>(async (code) => {
    const l = local.current;
    if (l) {
      const achado = await l.repo.findByCode(l.store, code).catch(() => null);
      if (achado) return achado;
    }
    const remotos = await pdvApi.searchVariants(code);
    return remotos.find((v) => v.barcode === code || v.sku === code) ?? null;
  }, []);

  return { buscar, porCodigo, fonte };
}
